import React, { useEffect, useMemo, useRef } from 'react';
import { arrayOf, shape, string, func, bool } from 'prop-types';
import { Field } from 'react-final-form';

// MUST import license registration BEFORE any Syncfusion components
import '../../util/syncfusionLicense';

// CRITICAL: Ensure Syncfusion CSS is loaded in browser
// This is a safety measure in case index.js CSS loading doesn't work
if (typeof window !== 'undefined') {
  try {
    require('@syncfusion/ej2-base/styles/material.css');
    require('@syncfusion/ej2-react-dropdowns/styles/material.css');
    console.log('✅ FieldSearchableSelect: Syncfusion CSS loaded via component');
  } catch (e) {
    console.warn('⚠️ FieldSearchableSelect: Could not load Syncfusion CSS:', e.message);
  }
}

import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';

import { ValidationError } from '../../components';

import css from './FieldSearchableSelect.module.css';

const FieldSearchableSelect = props => {
  const { options, input, meta, placeholder, onSelectionChange, autoOpen } = props;
  const dropdownRef = useRef(null);

  // Detect mobile once at mount — controls filtering and fullscreen behavior
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Auto-open dropdown when component becomes visible (FilterPopup opens)
  useEffect(() => {
    if (autoOpen && dropdownRef.current) {
      const timer = setTimeout(() => {
        if (dropdownRef.current) {
          dropdownRef.current.showPopup();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoOpen]);

  // Memoize to keep stable references for Syncfusion
  const syncfusionOptions = useMemo(
    () =>
      options
        .filter(opt => !opt.isDisabled)
        .map(opt => ({ value: opt.option, text: opt.label })),
    [options]
  );
  const fields = useMemo(() => ({ text: 'text', value: 'value' }), []);
  const selectedText = syncfusionOptions.find(o => o.value === input.value)?.text || null;

  return (
    <div className={css.root}>
      <DropDownListComponent
        ref={dropdownRef}
        dataSource={syncfusionOptions}
        fields={fields}
        value={input.value || null}
        text={selectedText}
        change={(args) => {
          const selectedValue = args.value || null;
          input.onChange(selectedValue);
          if (onSelectionChange) {
            if (dropdownRef.current) {
              dropdownRef.current.hidePopup();
            }
            onSelectionChange(selectedValue);
          }
        }}
        select={(args) => {
          // Fallback: on iOS the change event may not fire inside fixed modals.
          // The select event fires directly from user tap/click.
          console.log('🔍 FSS SELECT', args?.itemData?.value);
        }}
        blur={() => input.onBlur()}
        focus={() => input.onFocus()}
        placeholder={placeholder || 'Search...'}
        allowFiltering={!isMobile}
        filterType="Contains"
        ignoreCase={true}
        popupHeight="300px"
        isDeviceFullScreen={false}
      />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

FieldSearchableSelect.defaultProps = {
  options: [],
  onSelectionChange: null,
  autoOpen: false,
};

FieldSearchableSelect.propTypes = {
  options: arrayOf(
    shape({
      option: string.isRequired,
      label: string.isRequired,
    })
  ),
  onSelectionChange: func,
  autoOpen: bool,
};

export default FieldSearchableSelect;
